import { useCallback, useEffect, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { AjustesCitas, Cita, HorasEfectivas, Profesional } from '../../api/ops/citas';
import { todas } from './logica';

/**
 * Lo que lee la Agenda (9.1) y Mi agenda (9.8): los profesionales (las columnas; para el Profesional, el suyo por
 * `member_id`), los ajustes de Citas, las citas del día o la semana, las «Por completar» (G14) y, aparte y sin parar la
 * página si fallan, los tramos de cada profesional (`getEffectiveHours`) y los nombres de los clientes (`listCustomers`
 * con `ids`, una sola llamada, G303).
 */
export interface DatosAgenda {
    profesionales: Profesional[];
    /** El profesional de quien entra (Mi agenda), si lo es. */
    propio: Profesional | null;
    ajustes: AjustesCitas | null;
    citas: Cita[];
    porCompletar: Cita[];
    /** Con `?firma=pendiente` («Firmas en el centro», G960): las citas con la firma por hacer; si no, vacía. */
    firmasPendientes: Cita[];
    horas: Readonly<Record<string, HorasEfectivas>>;
    nombres: Readonly<Record<string, { nombre: string; telefono: string | null }>>;
}

export type EstadoDatos = { tipo: 'cargando' } | { tipo: 'error'; error: unknown } | { tipo: 'listo'; datos: DatosAgenda };

export function useDatosAgenda(ops: Operaciones, negocio: string, desde: string, hasta: string, opciones: { soloLoSuyo: boolean; memberId: string | null; firmas?: boolean }) {
    const [estado, setEstado] = useState<EstadoDatos>({ tipo: 'cargando' });
    const [intento, setIntento] = useState(0);
    const { soloLoSuyo, memberId } = opciones;
    const firmas = opciones.firmas === true;

    useEffect(() => {
        let vivo = true;
        const cargar = async (): Promise<DatosAgenda> => {
            const [profesionales, ajustes] = await Promise.all([
                todas((cursor) => ops.listProfessionals(negocio, soloLoSuyo && memberId !== null ? { member_id: memberId, cursor, limit: 100 } : { status: 'active', cursor, limit: 100 })),
                ops.getAppointmentSettings(negocio).catch(() => null),
            ]);
            const propio = soloLoSuyo ? (profesionales.find((p) => p.member_id === memberId) ?? profesionales[0] ?? null) : null;
            const filtroPropio = propio === null ? {} : { professional_id: propio.id };
            const [citas, porCompletar, firmasPendientes] = await Promise.all([
                todas((cursor) => ops.listAppointments(negocio, { from: desde, to: hasta, sort: 'starts_at', ...filtroPropio, cursor, limit: 100 })),
                ops.listAppointments(negocio, { status: 'pending_completion', sort: 'starts_at', ...filtroPropio, limit: 50 }).then((l) => l.data),
                firmas ? ops.listAppointments(negocio, { signature: 'pending', sort: 'starts_at', ...filtroPropio, limit: 50 }).then((l) => l.data) : Promise.resolve([] as Cita[]),
            ]);
            const columnas = propio === null ? profesionales.filter((p) => p.status === 'active') : [propio];
            const horas: Record<string, HorasEfectivas> = {};
            const ids = [...new Set([...citas, ...porCompletar, ...firmasPendientes].map((c) => c.customer_id).filter((x): x is string => x !== null))];
            await Promise.all([
                ...columnas.map((p) =>
                    ops
                        .getEffectiveHours(negocio, p.calendar_id, desde, hasta)
                        .then((h) => {
                            horas[p.id] = h;
                        })
                        .catch(() => undefined),
                ),
            ]);
            const nombres: Record<string, { nombre: string; telefono: string | null }> = {};
            if (ids.length > 0) {
                await ops
                    .listCustomersByIds(negocio, ids)
                    .then((l) => {
                        for (const c of l.data) {
                            nombres[c.id] = { nombre: c.display_name, telefono: c.phone ?? null };
                        }
                    })
                    .catch(() => undefined);
            }
            return { profesionales: columnas, propio, ajustes, citas, porCompletar, firmasPendientes, horas, nombres };
        };
        cargar()
            .then((datos) => {
                if (vivo) {
                    setEstado({ tipo: 'listo', datos });
                }
            })
            .catch((error: unknown) => {
                if (vivo) {
                    setEstado({ tipo: 'error', error });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, desde, hasta, soloLoSuyo, memberId, firmas, intento]);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);

    /** Pinta ya una cita que devolvió la API (sin esperar a volver a leer). */
    const ponerCita = useCallback((c: Cita) => {
        setEstado((e) => {
            if (e.tipo !== 'listo') {
                return e;
            }
            const cambia = (l: Cita[]) => (l.some((x) => x.id === c.id) ? l.map((x) => (x.id === c.id ? c : x)) : l);
            const porCompletar = c.status === 'pending_completion' ? (e.datos.porCompletar.some((x) => x.id === c.id) ? cambia(e.datos.porCompletar) : [...e.datos.porCompletar, c]) : e.datos.porCompletar.filter((x) => x.id !== c.id);
            return { tipo: 'listo', datos: { ...e.datos, citas: cambia(e.datos.citas), porCompletar } };
        });
    }, []);

    return { estado, recargar, ponerCita };
}
